import { InvoiceNotification } from '../types/invoiceNotification';

const toDisplayDate = (timestamp: any): string => {
  const value = Number(timestamp);
  if (!value) {
    return '';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const toDisplayTime = (hour: any, minute: any): string => {
  if (hour === undefined || minute === undefined || hour === null || minute === null) {
    return '';
  }

  const parsedHour = Number(hour);
  const parsedMinute = Number(minute);
  if (Number.isNaN(parsedHour) || Number.isNaN(parsedMinute)) {
    return '';
  }

  return `${String(parsedHour).padStart(2, '0')}:${String(parsedMinute).padStart(2, '0')}`;
};

const toNumber = (value: any): number | undefined => {
  if (value === undefined || value === null || value === '') {
    return undefined;
  }

  const parsed = Number(value);
  return Number.isNaN(parsed) ? undefined : parsed;
};

const buildNotificationContent = (item: InvoiceNotification): string => {
  const parts: string[] = [];

  if (item.tableName) {
    parts.push(`Bàn: ${item.tableName}`);
  }
  if (item.itemName) {
    parts.push(`Món: ${item.itemName}`);
  }
  if (item.quantity !== undefined) {
    parts.push(`SL: ${item.quantity}`);
  }
  if (item.lineTotal !== undefined) {
    parts.push(`Thành tiền: ${item.lineTotal.toLocaleString('vi-VN')} ${item.currency || 'VND'}`);
  }
  if (item.optionSummary) {
    parts.push(`Option: ${item.optionSummary}`);
  }

  return parts.join(' | ');
};

export const parseJsonToInvoiceNotifications = async (file: File): Promise<InvoiceNotification[]> => {
  const rawText = await file.text();
  const parsed = JSON.parse(rawText);

  const transactions = Array.isArray(parsed)
    ? parsed
    : Array.isArray(parsed?.data)
      ? parsed.data
      : [];

  const result: InvoiceNotification[] = [];

  transactions.forEach((transaction: any, transactionIndex: number) => {
    const saleDetails = Array.isArray(transaction?.sale_detail)
      ? transaction.sale_detail
      : Array.isArray(transaction?.saleDetail)
        ? transaction.saleDetail
        : [];

    const paymentMethods = Array.isArray(transaction?.sale_payment_method)
      ? transaction.sale_payment_method
      : [];
    const primaryPaymentMethod = paymentMethods[0];
    const transactionId = String(transaction?.tran_id || '').trim();
    const shortCode = transactionId ? `#${transactionId.slice(-5)}` : '';
    const billNumber = String(transaction?.tran_no || '').trim();
    const invoiceCodeValue = shortCode || billNumber || transactionId;

    saleDetails.forEach((detail: any, detailIndex: number) => {
      const invoiceCode = invoiceCodeValue;
      if (!invoiceCode) {
        return;
      }

      const quantity = toNumber(detail?.quantity);
      const unitPrice = toNumber(detail?.price_org ?? detail?.price);
      const lineTotal = toNumber(detail?.amount ?? (quantity || 0) * (unitPrice || 0));
      const lineKey = `${transaction?.tran_id || invoiceCode}__${detail?.id || `${transactionIndex + 1}-${detailIndex + 1}`}`;
      const selectedOptions = Array.isArray(detail?.toppings)
        ? detail.toppings.map((option: any) => ({
            optionId: option?.item_id ? String(option.item_id).trim() : '',
            optionName: String(option?.item_name || '').trim(),
            quantity: toNumber(option?.quantity),
            unit: option?.unit_id ? String(option.unit_id).trim() : '',
            amount: toNumber(option?.amount),
            price: toNumber(option?.price_org ?? option?.price),
          })).filter((option: any) => option.optionName)
        : [];
      const optionSummary = selectedOptions.map((option: any) => option.optionName).join(', ');

      const record: InvoiceNotification = {
        lineKey,
        stt: String(result.length + 1),
        store: String(transaction?.store_name || transaction?.storeName || '').trim(),
        storeUid: String(transaction?.store_uid || transaction?.storeUid || '').trim(),
        shiftCode: String(transaction?.shift_name || transaction?.shiftName || transaction?.shift_code || transaction?.shiftCode || transaction?.shift_id || '').trim(),
        shiftName: String(transaction?.shift_name || transaction?.shiftName || '').trim(),
        invoiceCode,
        billNumber,
        guestCount: toNumber(transaction?.extra_data?.peo_count ?? transaction?.extra_data?.people_count),
        employeeName: String(transaction?.employee_name || '').trim(),
        vatInfo: transaction?.vat_invoice_series ? String(transaction.vat_invoice_series).trim() : '',
        eInvoiceNumber: String(transaction?.vat_invoice_number || '').trim(),
        checkInDate: toDisplayDate(transaction?.start_date || transaction?.tran_date),
        checkInTime: toDisplayTime(transaction?.start_hour, transaction?.start_minute),
        checkOutDate: toDisplayDate(transaction?.tran_date),
        checkOutTime: toDisplayTime(transaction?.end_hour, transaction?.end_minute),
        vatIssuedAt: transaction?.vat_invoice_date ? String(transaction.vat_invoice_date) : '',
        tableName: String(transaction?.table_name || '').trim(),
        status: transaction?.deleted ? 'deleted' : 'paid',
        rootInvoiceCode: String(transaction?.origin_tran_id || '').trim(),
        itemName: String(detail?.item_name || '').trim(),
        itemId: String(detail?.item_id || '').trim(),
        itemTypeId: String(detail?.item_type_id || '').trim(),
        itemClassId: String(detail?.item_class_id || '').trim(),
        quantity,
        unit: String(detail?.unit_id || '').trim(),
        unitPrice,
        lineTotal,
        vatRate: toNumber(detail?.vat_tax_rate),
        note: String(detail?.note || transaction?.sale_note || '').trim(),
        selectedOptions,
        optionSummary,
        paymentMethodName: String(primaryPaymentMethod?.payment_method_name || '').trim(),
        paymentMethodId: String(primaryPaymentMethod?.payment_method_id || '').trim(),
        orderCode: transactionId,
        customerName: String(transaction?.extra_data?.customer_name || '').trim(),
        customerPhone: String(transaction?.extra_data?.customer_phone || '').trim(),
        saleDate: toDisplayDate(transaction?.tran_date),
        totalAmount: toNumber(transaction?.total_amount),
        currency: String(transaction?.currency || 'VND').trim() || 'VND',
        channel: String(transaction?.source_deli || '').trim(),
        notificationTitle: `Hóa đơn ${invoiceCode} - ${String(detail?.item_name || '').trim() || 'Mặt hàng'}`,
        notificationContent: '',
        isActive: true,
        rawData: {
          transactionId: transaction?.tran_id,
          detailId: detail?.id,
          optionCount: selectedOptions.length,
        },
      };

      record.notificationContent = buildNotificationContent(record);
      result.push(record);
    });
  });

  return result;
};
